import type { UseFormRegisterReturn } from "react-hook-form";

import * as React from "react";

import { LabeledControl } from "@replohq/lattice/blocks/labeled-control/LabeledControl";
import { Input } from "@replohq/lattice/components/input/Input";
import { Separator } from "@replohq/lattice/components/separator/Separator";
import GoogleAuth from "@v2/features/account/GoogleAuth";
import { TermsAndPrivacy } from "@v2/features/account/TermsAndPrivacy";

import { StepActions } from "./StepActions";

export type AgentBetaAccountMode = "signup" | "login";

// NOTE (Yuxin, 2026-07-18): Props-based, not form-object based, so waitlist and enable can share it despite different form shapes.
export const AccountStep: React.FC<{
  emailProps: UseFormRegisterReturn;
  passwordProps: UseFormRegisterReturn;
  emailError?: string;
  passwordError?: string;
  accountMode: AgentBetaAccountMode;
  isSubmitting: boolean;
  submitLabels?: { signup: string; login: string };
  onModeChange: (mode: AgentBetaAccountMode) => void;
  onSubmit: () => void;
  onGoogleSubmit: React.ComponentProps<
    typeof GoogleAuth
  >["handleGoogleCredentials"];
}> = ({
  emailProps,
  passwordProps,
  emailError,
  passwordError,
  accountMode,
  isSubmitting,
  submitLabels = { signup: "Lock in my spot", login: "Sign in" },
  onModeChange,
  onSubmit,
  onGoogleSubmit,
}) => (
  <form
    className="flex w-full flex-col gap-5"
    onSubmit={(event) => {
      event.preventDefault();
      onSubmit();
    }}
  >
    <LabeledControl
      label="Email"
      size="base"
      id="accountEmail"
      error={emailError}
    >
      <Input
        id="accountEmail"
        type="email"
        size="base"
        autoComplete="off"
        placeholder="Your work email"
        invalid={Boolean(emailError)}
        {...emailProps}
      />
    </LabeledControl>
    <LabeledControl
      label="Password"
      size="base"
      id="password"
      error={passwordError}
    >
      <Input
        id="password"
        type="password"
        size="base"
        autoComplete="off"
        placeholder="••••••••••••"
        invalid={Boolean(passwordError)}
        {...passwordProps}
      />
    </LabeledControl>

    {accountMode === "signup" && <TermsAndPrivacy />}

    <StepActions
      submitLabel={
        accountMode === "signup" ? submitLabels.signup : submitLabels.login
      }
      isSubmitting={isSubmitting}
    />

    <Separator />
    <GoogleAuth handleGoogleCredentials={onGoogleSubmit} />

    <div className="text-sm text-placeholder">
      {accountMode === "signup"
        ? "Already have a Replo account?"
        : "Don't have a Replo account?"}
      <button
        type="button"
        className="text-primary cursor-pointer pl-2"
        onClick={() => {
          onModeChange(accountMode === "signup" ? "login" : "signup");
        }}
      >
        {accountMode === "signup" ? "Sign in" : "Create one"}
      </button>
    </div>
  </form>
);
